import axios from "axios";
import { queryParams } from "./queryParams";
import noop from "lodash/noop";
import omit from "lodash/omit";

export default class PollingRequest {
  constructor(options) {
    const query = options.query || {};

    this.options = options;
    this.options.query = JSON.parse(JSON.stringify(query));
    this.options.interval = options.interval || 1000;
    this.options.success = options.success || noop;
    this.options.progress = options.progress || noop;
    this.options.error = options.error || noop;
    this.data = [];
    this.started = false;
    this.initRequest = false;
    this.currentPage = 0;
    this.totalPages = 0;
    this.numOfRequests = 10;
    this.finalRequestUrl = null;
    this.doFinalRequest = false;
    this.headerInfo = {
      title: "",
      value: 0
    };
  }

  start(cb = noop) {
    if (!this.started) {
      this.started = true;
      cb();
    }

    if (!this.timer) {
      this.timer = setTimeout(() => {
        this.run();
      }, this.interval);
    }
  }

  clearTimer() {
    clearTimeout(this.timer);
    this.timer = undefined;
  }

  handleProgress(response) {
    const { pagination, tableInfo } = response;

    if (this.initRequest) {
      this.currentPage = this.currentPage + response.length;
      this.nextLinks = this.nextLinks.slice(this.numOfRequests);

      response.map((item) => {
        this.data = this.data.concat(item.data.tableInfo.data);
      });
      this.options.progress({
        current: this.currentPage,
        total: this.totalPages
      });

      if (this.currentPage >= this.totalPages) {
        this.clearTimer();
        return false;
      } else if (this.currentPage === this.totalPages - 1) {
        this.doFinalRequest = true;
        this.clearTimer();
        this.start();
        return true;
      }
      this.clearTimer();
      this.start();
      return true;
    }

    if (pagination && pagination.page === 1 && pagination.nextPageLinks) {
      this.finalRequestUrl = pagination.nextPageLinks.slice(-1).pop();
      this.initRequest = true;
      this.totalPages = pagination.pages;
      this.currentPage = 1;
    }

    if (pagination && pagination.nextPageLinks) {
      this.nextLinks = pagination.nextPageLinks.slice(0, -1);
      this.data = this.data.concat(tableInfo.data);

      this.options.progress({
        current: pagination.page,
        total: pagination.pages
      });

      this.clearTimer();
      this.start();
      return true;
    } else {
      this.data = this.data.concat(tableInfo.data);
      this.clearTimer();
      return false;
    }
  }

  handleFinished(data) {
    this.options.success(data);
    this.clearTimer();
  }

  // Generic hook for any raw data view whose per-row value for some column can't be
  // known until every paginated location's page has landed (e.g. UniqueMembersController
  // "Moved Member" — a cross-location comparison impossible from a single-location call,
  // see PBI-68833). Once the final page arrives, its response may carry a `backfills`
  // array describing corrections to apply to the already-merged rows before the table
  // ever renders — no-op for every view that doesn't set it. Each entry:
  //   { matchColumn: "Customer ID", targetColumn: "Moved Member", matchedValue: "Y", keys: [...] }
  // meaning: for every row whose matchColumn value is in `keys`, set targetColumn to
  // matchedValue. The backend owns the column names/values entirely — this method has
  // no knowledge of "Moved Member" or any other specific feature.
  applyBackfills(data) {
    const backfills = (data && data.backfills) || [];
    if (!backfills.length) return;

    const { colHeaders, tableConfiguration } = (data && data.tableInfo) || {};
    if (!colHeaders) return;

    const linkColumns = (tableConfiguration && tableConfiguration.linkColumns) || [];
    if (linkColumns.length > 1) {
      console.warn("applyBackfills: skipping, more than one linkColumns entry is not supported yet");
      return;
    }

    // colHeaders under-counts columns positioned after a link column: the row array
    // carries an extra (hidden) URL value per link column that has no colHeaders entry
    // of its own — the raw data table splices a synthetic header back in for it at
    // render time (rawDataTableModule/smartComponents/rawDataTable.js). Replicate that
    // same shift here so we write to the column HandsonTable will actually render.
    const trueRowIndex = (columnName) => {
      const rawIndex = colHeaders.indexOf(columnName);
      if (rawIndex === -1) return -1;

      const shiftsBefore = linkColumns.filter((col) => colHeaders.indexOf(col) < rawIndex).length;
      return rawIndex + shiftsBefore;
    };

    backfills.forEach(({ matchColumn, targetColumn, matchedValue, keys }) => {
      const matchIndex = trueRowIndex(matchColumn);
      const targetIndex = trueRowIndex(targetColumn);
      if (matchIndex === -1 || targetIndex === -1 || !keys) return;

      this.data.forEach((row) => {
        if (keys.includes(row[matchIndex])) {
          row[targetIndex] = matchedValue;
        }
      });
    });
  }

  // Common "we're done, hand the merged dataset off" path for both places polling can
  // terminate (the delayed final-page request, and a batch that completes without one).
  // `data` is the one response object whose top-level fields (headerInfo, pagination,
  // backfills, ...) become the final payload — its tableInfo.data gets replaced by the
  // fully-merged row array.
  finish(data) {
    this.applyBackfills(data);
    data.tableInfo.data = this.data;
    this.handleFinished(data);
  }

  handleFail(errors) {
    this.options.error(errors);
    this.clearTimer();
  }

  async run() {
    let rawDatapath;
    let rawParams = this.getShortParams();
    const { query } = this.options;
    const baseUrl = `${queryParams.apiVersion}${query.data_url}`;
    const shortUrl = query.short_url;

    if (this.initRequest) {
      let requests = [];
      this.nextLinks.map((item, index) => {
        // We don't need to send params for the next links in pagination
        // rawParams = this.getParams();
        rawParams = {};
        if (index < this.numOfRequests)
          requests.push(axios.get(item, { params: rawParams, paramsSerializer }));
      });

      if (this.doFinalRequest) {
        // need to set a timeout because sometimes BE isn't ready to return calculated total headers.
        setTimeout(async () => {
          try {
            const result = await axios.get(this.finalRequestUrl, {
              params: rawParams,
              paramsSerializer
            });

            this.data = this.data.concat(result.data.tableInfo.data);

            this.finish(result.data);
          } catch (err) {
            this.handleFail(err);
          }
        }, 500);
      } else {
        try {
          const result = await axios.all(requests);
          const canContinue = this.handleProgress(result);

          if (!canContinue) {
            // result[0] is positional, not necessarily the response whose backend check
            // actually saw every sibling page persisted — a concurrent batch member may
            // have landed backfills while result[0] didn't. Prefer whichever response
            // actually carries it; every other raw data view never sets it, so this
            // falls back to today's result[0] behavior unchanged.
            const winner = result.find((item) => item.data.backfills && item.data.backfills.length) || result[0];
            this.finish(winner.data);
          }
        } catch (err) {
          this.handleFail(err);
        }
      }
    } else {
      if (shortUrl && this.nextLink) {
        rawDatapath = this.nextLink;
      } else if (shortUrl) {
        rawDatapath = baseUrl + "?short_url=" + shortUrl;
      } else {
        rawDatapath = this.nextLink || baseUrl;
        rawParams = this.getParams();
      }

      try {
        const result = await axios.get(rawDatapath, { params: rawParams, paramsSerializer });

        if (result.data.pagination === undefined) {
          this.handleFinished(result.data);
        } else if (!result.data.pagination.enabled) {
          this.handleFinished(result.data);
        } else {
          this.handleProgress(result.data);
        }
      } catch (err) {
        this.handleFail(err);
      }
    }
  }

  getParams() {
    const { query } = this.options;

    return {
      organization_id: query.organizationId,
      category_ids: query.category_ids,
      // location_id: query.locationId,
      // object_id: query.objectId,
      // region_id: query.regionId,
      date: query.date,
      start_date: query.start_date,
      end_date: query.end_date,
      search_start: query.search_start,
      search_end: query.search_end,

      year: query.year,
      month: query.month,
      day: query.day,

      shift_months: query.shift_months,
      period: query.period,
      period_days: query.period_days,
      period_type: query.period_type,

      filters: query.filters,
      chart_name: query.chart_name,
      view: query.view,

      category_type: query.category_type,
      category_type_id: query.category_type_id,

      exclude_offers: query.exclude_offers,
      exclude_intros: query.exclude_intros,
      include_returns: query.include_returns,
      count_no_shows: query.count_no_shows,
      show_expenses: query.show_expenses,
      include_cross_regional_visits: query.include_cross_regional_visits,

      dashboard_short_url: query.dashboard_short_url,
      short_url: query.dashboard_short_url
    };
  }

  getShortParams() {
    const { query } = this.options;

    return {
      organization_id: query.organizationId
    };
  }
}

function paramsSerializer(params) {
  const { filters } = params;
  const plainObj = omit(params, ["filters"]);

  return `${jQuery.param(plainObj)}&${jQuery.param({ filters })}`;
}
